import React, { useState, useEffect } from 'react';
import { useThemeClasses } from '../lib/themes/utils';
import { Navigation } from './common/Navigation';
import { Footer } from './common/Footer';

interface GroupSession {
  id: string;
  title: string;
  description: string;
  price: number;
  session_date: string;
  available_spots: number;
  max_participants: number;
  location: string;
  appointment_type_id: number;
  group_session_id: number;
}

export const CommunityPage: React.FC = () => {
  const [groupSessions, setGroupSessions] = useState<GroupSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [showPurchaseForm, setShowPurchaseForm] = useState<string | null>(null);
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerName, setCustomerName] = useState('');
  
  const theme = useThemeClasses();

  useEffect(() => {
    fetchGroupSessions();
  }, []);

  const fetchGroupSessions = async () => {
    try {
      const response = await fetch('/api/content-catalog');
      
      if (!response.ok) {
        throw new Error('Failed to fetch group sessions');
      }

      const data = await response.json();
      setGroupSessions(data.success ? data.data.group_sessions : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load group sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleGroupSessionBooking = async (session: GroupSession) => {
    if (!customerEmail) {
      setError('Please enter your email address to book a group session');
      return;
    }

    setPurchasing(session.id);
    setError(null);

    try {
      const response = await fetch('/api/public-book-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          appointmentTypeId: session.appointment_type_id,
          groupSessionId: session.group_session_id,
          customerEmail,
          customerName,
          sessionType: 'group'
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        if (data.should_login) {
          setError(`${data.error} Click here to log in instead.`);
        } else if (data.pending_booking) {
          setError(data.error);
        } else if (data.recent_booking) {
          setError(`${data.error} If you completed payment, check your email for confirmation.`);
        } else {
          throw new Error(data.error || 'Failed to book group session');
        }
        return;
      }
      
      if (data.success && data.data.checkout_url) {
        window.location.href = data.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Booking failed');
    } finally {
      setPurchasing(null);
    }
  };

  const formatPrice = (priceInCents: number) => {
    return `$${(priceInCents / 100).toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${theme.backgrounds.gradient} flex justify-center items-center`}>
        <div className="text-center">
          <div className="text-6xl mb-4 animate-pulse">👥</div>
          <div className={`text-xl ${theme.typography.heading}`}>Loading community sessions...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme.backgrounds.gradient}`}>
      <Navigation />
      
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-secondary-50 via-white to-secondary-100">
        {/* Animated background elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-secondary-100/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary-200/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
          <svg className="absolute top-20 right-32 w-32 h-32 text-secondary-200 opacity-20 animate-float" viewBox="0 0 100 100">
            <path d="M50 20 Q 30 30 20 50 T 30 80 L 50 90 L 70 80 Q 80 60 80 50 T 70 30 Z" fill="currentColor" />
          </svg>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-white/50 backdrop-blur-sm rounded-full">
              <span className="text-secondary-600 text-sm font-medium">Community Workshops</span>
            </div>
            
            <h1 className="text-5xl sm:text-6xl font-light text-gray-900 mb-6 tracking-tight">
              Heal <span className="italic text-secondary-600">Together</span>
              <br />
              <span className="font-normal">in Sacred Community</span>
            </h1>
            
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Join intimate healing circles where wisdom is shared, connections are made, 
              and transformation happens collectively. Experience the power of community 
              support on your wellness journey.
            </p>
          </div>
        </div>
      </div>

      {/* Why Group Sessions Section */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-light text-gray-900 mb-4">
              The Power of Collective Healing
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Sometimes the most profound healing happens when we realize we're not alone
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">🤝</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Shared Experiences
              </h3>
              <p className="text-gray-600">
                Connect with others on similar healing journeys. Share stories, challenges, 
                and victories in a safe, supportive environment.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">💡</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Collective Wisdom
              </h3>
              <p className="text-gray-600">
                Learn from both professional guidance and peer insights. Everyone brings 
                unique perspectives that enrich the healing process.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">🌟</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Affordable Access
              </h3>
              <p className="text-gray-600">
                Group sessions make holistic healing more accessible, offering professional 
                guidance at a fraction of the cost of individual sessions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Sessions */}
      <section className="py-20 bg-gradient-to-b from-white to-secondary-50/30">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          {error && (
            <div className="bg-red-50/80 backdrop-blur-sm border border-red-200 text-red-700 px-6 py-4 rounded-xl mb-8 shadow-sm">
              {error}
              <button 
                onClick={() => setError(null)}
                className="ml-3 text-red-500 hover:text-red-700 transition-colors"
              >
                ✕
              </button>
            </div>
          )}

          <div className="text-center mb-12">
            <h2 className="text-4xl font-light text-gray-900 mb-4">
              Upcoming Group Sessions
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Reserve your spot in our intimate healing circles. All sessions are limited 
              to ensure personalized attention and meaningful connections.
            </p>
          </div>

          {groupSessions.length === 0 ? (
            <div className="bg-white rounded-3xl p-16 text-center shadow-lg">
              <div className="text-secondary-300 text-7xl mb-6">🌸</div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">New Sessions Coming Soon</h3>
              <p className="text-gray-600 max-w-md mx-auto leading-relaxed">
                We're planning our next series of transformative group workshops. 
                Join our mailing list to be the first to know when registration opens.
              </p>
            </div>
          ) : (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {groupSessions.map((session) => (
                <div key={session.id} className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
                  <div className="p-8">
                    <div className="mb-6">
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">
                        {session.title}
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {session.description}
                      </p>
                    </div>

                    <div className="space-y-3 mb-6">
                      <div className="flex items-center gap-3 text-sm">
                        <svg className="w-5 h-5 text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-gray-700">
                          {new Date(session.session_date).toLocaleDateString('en-US', {
                            weekday: 'long',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-3 text-sm">
                        <svg className="w-5 h-5 text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-gray-700">
                          {new Date(session.session_date).toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                            timeZoneName: 'short'
                          })}
                        </span>
                      </div>

                      <div className="flex items-center gap-3 text-sm">
                        <svg className="w-5 h-5 text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <span className={session.available_spots > 0 ? 'text-gray-700' : 'text-red-600 font-medium'}>
                          {session.available_spots > 0 
                            ? `${session.available_spots} of ${session.max_participants} spots available`
                            : 'Session Full'
                          }
                        </span>
                      </div>

                      {session.location && (
                        <div className="flex items-center gap-3 text-sm">
                          <svg className="w-5 h-5 text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span className="text-gray-700">{session.location}</span>
                        </div>
                      )}
                    </div>

                    <div className="border-t pt-6">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-2xl font-semibold text-gray-900">
                          {formatPrice(session.price)}
                        </span>
                        <span className="text-sm text-gray-500">per person</span>
                      </div>

                      {showPurchaseForm === session.id ? (
                        <div className="space-y-3">
                          <input
                            type="email"
                            placeholder="Your email"
                            value={customerEmail}
                            onChange={(e) => setCustomerEmail(e.target.value)}
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-secondary-500 focus:ring-2 focus:ring-secondary-200 transition-colors"
                            required
                          />
                          <input
                            type="text"
                            placeholder="Your name (optional)"
                            value={customerName}
                            onChange={(e) => setCustomerName(e.target.value)}
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-secondary-500 focus:ring-2 focus:ring-secondary-200 transition-colors"
                          />
                          <div className="flex gap-3">
                            <button
                              onClick={() => handleGroupSessionBooking(session)}
                              disabled={purchasing === session.id || !customerEmail}
                              className="flex-1 bg-gradient-to-r from-secondary-500 to-secondary-600 text-white px-4 py-3 rounded-lg font-medium hover:from-secondary-600 hover:to-secondary-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {purchasing === session.id ? 'Processing...' : 'Book Your Spot'}
                            </button>
                            <button
                              onClick={() => setShowPurchaseForm(null)}
                              className="px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => session.available_spots <= 0 ? null : setShowPurchaseForm(session.id)}
                          disabled={session.available_spots <= 0}
                          className="w-full bg-gradient-to-r from-secondary-500 to-secondary-600 text-white px-6 py-3 rounded-lg font-medium hover:from-secondary-600 hover:to-secondary-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {session.available_spots <= 0 ? 'Session Full' : 'Reserve Your Spot'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-light text-gray-900 mb-4">
              What to Expect
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our group sessions blend education, experiential learning, and community support
            </p>
          </div>

          <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-3">
                <span className="text-2xl">📚</span>
                Educational Content
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Each session includes evidence-based information presented in an accessible, 
                engaging way. Learn practical tools you can implement immediately.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-3">
                <span className="text-2xl">🧘‍♀️</span>
                Experiential Practices
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Participate in guided meditations, breathwork, movement practices, and other 
                embodied experiences that deepen your understanding.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-3">
                <span className="text-2xl">💬</span>
                Group Sharing
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Optional opportunities to share your experiences and learn from others in a 
                safe, confidential, and supportive environment.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-3">
                <span className="text-2xl">📝</span>
                Take-Home Resources
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Receive handouts, protocols, and practices to continue your healing journey 
                between sessions and beyond.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-t from-secondary-50 to-white">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <h2 className="text-3xl font-light text-gray-900 mb-6">
            Not Sure About Group Sessions?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            If you prefer individualized attention or have complex health needs, 
            our 1:1 consultation packages might be a better fit.
          </p>
          <a 
            href="/personalized" 
            className="inline-flex items-center gap-3 bg-white border-2 border-primary-500 text-primary-600 px-8 py-4 rounded-xl font-medium text-lg hover:bg-primary-50 transition-all"
          >
            <span>Explore 1:1 Options</span>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </a>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};